/**
 * THE CONTRACT. (Brief §15.2)
 *
 * Every module in /sim, /net, the client, the server and the AI codes against
 * these types. They are frozen: if you need to change a shape here, that is a
 * cross-cutting change and must be made deliberately, not drive-by.
 *
 * Design notes that matter:
 *
 * 1. WorldState is PLAIN OLD DATA backed by typed arrays (structure-of-arrays).
 *    This gives us three things the netcode requires:
 *      - cloning is a buffer copy (no GC churn in a 60Hz loop, §15.7)
 *      - hashing is an FNV-1a walk over the buffers (§13.1 determinism test)
 *      - snapshot/restore for reconciliation is trivial (§2.4)
 *    Do NOT add class instances, Maps, or closures to WorldState.
 *
 * 2. Rapier is used as a COLLISION/QUERY engine, not the owner of state. We
 *    push our POD state in, step, and read results back out. This keeps
 *    snapshot/restore cheap. See physics.ts for the full rationale.
 *
 * 3. AI produces an InputCommand identical in shape to a human's (§10.1). This
 *    is load-bearing: it guarantees AI can never be superhuman and makes
 *    drop-in takeover seamless.
 */

import type { Pcg32 } from '../math/pcg32.js';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** Max entities. 11v11 = 22 outfield+GK. Fixed capacity → no allocation. */
export const MAX_PLAYERS = 22;

export type Team = 0 | 1;
export type PlayerId = number; // index into the SoA arrays, 0..MAX_PLAYERS-1

// ---------------------------------------------------------------------------
// Input — the ONLY thing that mutates the world. (§10.1)
// ---------------------------------------------------------------------------

/**
 * Bit flags for boolean inputs — bit-packed on the wire. (§2.8)
 * Order is part of the protocol; append only, never reorder.
 */
export const enum InputFlags {
  None = 0,
  Sprint = 1 << 0,
  Pass = 1 << 1,
  Shoot = 1 << 2,
  ThroughBall = 1 << 3,
  SlideTackle = 1 << 4,
  StandingTackle = 1 << 5,
  Jockey = 1 << 6,
  SkillModifier = 1 << 7,
  CallForPass = 1 << 8,
  FirstTouch = 1 << 9,
  SwitchPlayer = 1 << 10,
  GkDive = 1 << 11,
  GkRush = 1 << 12,
  GkPunch = 1 << 13,
  /**
   * Arcade "funny addon" (house rule, not a Law of the Game): strike an
   * opposing player directly rather than the ball. ALWAYS a foul on contact,
   * unconditionally — unlike tackles, there is no clean version. See
   * rules/fouls.ts VIOLENT_CONDUCT_ACTIONS and DECISIONS.md D3.
   */
  PunchPlayer = 1 << 14,
  KickPlayer = 1 << 15,
  /**
   * Space (house controls, 2026-07-17; through-ball moved to Q). Grounded
   * players only; enables headers via the raised strike envelope. ⚠ Bits 16+
   * exceed a 16-bit wire field — the input codec must carry ≥18 bits
   * (PROTOCOL_VERSION bumped with the roster/switch changes).
   */
  Jump = 1 << 16,
  /**
   * Composed CLIENT-SIDE from B(hold)+Space+Shoot-release. The sim is the
   * authority on legality (airborne ball in the overhead envelope etc.) —
   * an illegal request simply falls through to nothing.
   */
  BicycleKick = 1 << 17,
  /**
   * THE STAMINA BOOST (owner, 2026-08-13). Key `1` once for tier 1, twice for
   * tier 2: more pace, bought with a much steeper stamina burn.
   *
   * TWO FLAGS AND NOT A NUMBER, because `InputCommand` is bit-packed on the wire
   * and appending two bits costs nothing while a new integer field costs a codec
   * change. Tier 2 sets BOTH, so a reader that only knows Boost1 still sees "he
   * is boosting" rather than "he is not" — the safe direction to be wrong in.
   *
   * ⚠ APPEND ONLY. Bit order is the protocol (§2.8).
   */
  Boost1 = 1 << 18,
  Boost2 = 1 << 19,

  /**
   * CLOSE CONTROL — Ctrl (owner: "when player is running with ball and he
   * press ctrl he takes the ball with dribbling").
   *
   * It also answers a second report — "stamina is burning on walk and not
   * getting recharged on walk" — because there was no walk to burn on. A key
   * press sends moveMag 1.0, so every keyboard step is a full 6 m/s run and
   * drains; the free jog band (1.4-3.5 m/s) was reachable only on a gamepad
   * half-press. Close control caps him inside that band, so the same key that
   * tightens the dribble is the one that lets him recover.
   */
  CloseControl = 1 << 20,

  /** Keeper: dive left / right (C and V). Direction is a FLAG rather than the
   *  move axis because a keeper dives across his goal while facing the shot —
   *  his movement input is not where he is going. */
  GkDiveLeft = 1 << 21,
  GkDiveRight = 1 << 22,
  /** Keeper: claim the ball with his hands (X). Deliberate only — a save on a
   *  shot stays automatic, because a human cannot react inside the window. */
  GkHold = 1 << 23,
}

/**
 * The highest bit `InputFlags` currently uses. ⚠ RAISE THIS WITH THE ENUM.
 *
 * It exists because the enum is `const` and therefore cannot be enumerated at
 * runtime — so nothing could check that the wire field is wide enough for it,
 * and TWICE now flags have been appended past the codec and silently masked
 * off: Jump/BicycleKick (found in play), then Boost1/Boost2, which made the
 * whole two-tier boost a no-op in online matches while working perfectly in
 * solo. `net/input.test.ts` asserts WIRE_INPUT_FLAGS_BITS covers this, so the
 * next flag past the wire fails CI instead of shipping.
 */
export const INPUT_FLAGS_HIGHEST_BIT = 23;

export const enum SkillMove {
  None = 0,
  BodyFeintLeft = 1,
  BodyFeintRight = 2,
  BallRoll = 3,
  DragBack = 4,
  StepOver = 5,
  Elastico = 6,
  KnockAndRun = 7,
  RabonaFakeShot = 8,
}

export const enum TacticalCommand {
  None = 0,
  PressHigh = 1,
  DropDeep = 2,
  OffsideTrap = 3,
  ManMark = 4,
  /** §5 team CIRCULATE — a single middle-click: keep the ball moving (the carrier
   *  plays a safe retain pass). Fits the 3-bit wire (0..7). */
  CirculatePass = 5,
}

/**
 * One tick of intent from one controller (human or AI).
 * MUST be serializable to a compact bit-packed form. No JSON on the wire (§15.4).
 */
export interface InputCommand {
  /** Monotonic per-client. Used for reconciliation. (§2.3) */
  seq: number;
  /** Sim tick this input was sampled for. */
  tick: number;
  /** Movement axis, camera-relative, each in [-1, 1]. Magnitude clamped to 1. */
  moveX: number;
  moveZ: number;
  /** Facing/aim yaw in radians, [-π, π]. From mouse or right stick. */
  aimYaw: number;
  /** Shot height / lift bias from mouse wheel, [-1, 1]. (§4.1) */
  liftBias: number;
  /**
   * Continuous curve bias, [-1, 1] — lateral mouse motion accumulated during
   * the shoot charge (house control scheme, see README "Controls"). Biases
   * strike-offset spin (§3.2) on top of aim; client clamps the accumulation
   * so this stays a subtle nudge, not a substitute for the Finesse modifier.
   * Zero for AI and for any input path that doesn't produce it (e.g. pass).
   */
  curl: number;
  /** Charge accumulators in seconds — the client charges, the server validates. */
  passCharge: number;
  shootCharge: number;
  /** Bitfield of InputFlags. */
  flags: number;
  /** Active skill move this tick. */
  skill: SkillMove;
  /** Quick tactical command (1-4 keys). (§4.3, §8.3) */
  tactical: TacticalCommand;
}

/** Zeroed input — used for pooling and for disconnected/idle slots. */
export function emptyInput(seq = 0, tick = 0): InputCommand {
  return {
    seq,
    tick,
    moveX: 0,
    moveZ: 0,
    aimYaw: 0,
    liftBias: 0,
    curl: 0,
    passCharge: 0,
    shootCharge: 0,
    flags: 0,
    skill: 0,
    tactical: 0,
  };
}

// ---------------------------------------------------------------------------
// Attributes (§8.1) — every one maps to a real sim parameter. No cosmetic stats.
// ---------------------------------------------------------------------------

export interface Attributes {
  // PHYSICAL
  pace: number;
  acceleration: number;
  stamina: number;
  strength: number;
  agility: number;
  balance: number;
  jumping: number;
  // TECHNICAL
  passing: number;
  shooting: number;
  dribbling: number;
  firstTouch: number;
  crossing: number;
  finishing: number;
  longShots: number;
  freeKick: number;
  penalties: number;
  heading: number;
  // MENTAL
  vision: number;
  composure: number;
  positioning: number;
  reactions: number;
  aggression: number;
  workRate: number;
  // DEFENSIVE
  tackling: number;
  marking: number;
  interceptions: number;
  standingTackle: number;
  slidingTackle: number;
  // GOALKEEPER
  reflexes: number;
  handling: number;
  diving: number;
  kicking: number;
  gkPositioning: number;
  oneOnOne: number;
}

export type AttributeName = keyof Attributes;

// ---------------------------------------------------------------------------
// Positions & roles (§8.2)
// ---------------------------------------------------------------------------

export type Position =
  | 'GK'
  | 'CB' | 'LB' | 'RB' | 'LWB' | 'RWB'
  | 'CDM' | 'CM' | 'CAM' | 'LM' | 'RM'
  | 'LW' | 'RW' | 'CF' | 'ST';

export type Role =
  | 'Standard'
  | 'BallPlayingCB'
  | 'InvertedFullback'
  | 'BoxToBox'
  | 'Regista'
  | 'False9'
  | 'Poacher'
  | 'TargetMan'
  | 'InsideForward'
  | 'SweeperKeeper';

// ---------------------------------------------------------------------------
// Animation state — 6-bit enum on the wire. (§2.8)
// ---------------------------------------------------------------------------

export const enum AnimState {
  Idle = 0,
  Walk = 1,
  Jog = 2,
  Run = 3,
  Sprint = 4,
  StrafeLeft = 5,
  StrafeRight = 6,
  Pass = 7,
  Shoot = 8,
  Volley = 9,
  Header = 10,
  StandingTackle = 11,
  SlideTackle = 12,
  SlideRecover = 13,
  Jockey = 14,
  Celebrate = 15,
  Skill = 16,
  /**
   * The throw-in RELEASE — two hands, from behind and over the head (Law 15).
   * Present since the first types.ts and, until now, never set by anything:
   * constants.ts's THROW_ANIM_HOLD_S already documents itself as "how long the
   * thrower holds the throw pose (drives AnimState.Throw)", so the member and
   * its hold constant were both authored for this and only the wiring was
   * missing — the reported "there is no aniamtion for that". Armed by
   * rules/fsm.ts runThrowInReady, then held through its authored contact beat;
   * executeThrowIn detaches the ball at that beat rather than on frame zero.
   * See `ThrowInHold` (33) for the raised-ball pose that precedes it.
   */
  Throw = 17,
  GkIdle = 18,
  GkDiveLeft = 19,
  GkDiveRight = 20,
  GkCatch = 21,
  GkPunch = 22,
  GkThrow = 23,
  GkKick = 24,
  Fallen = 25,
  Fatigued = 26,
  /** House-rule "funny addon": the attacker's punch swing at an opponent. */
  Punch = 27,
  /** House-rule "funny addon": the attacker's kick swing at an opponent. */
  Kick = 28,
  /** Airborne off a Space jump (outfield aerial duels / headers). */
  Jump = 29,
  /** Overhead scissor volley (B+Space+Shoot). High power, low accuracy. */
  BicycleKick = 30,
  /**
   * Off balance after losing a shoulder barge (§3.3, §4.4) — a lost stride, NOT
   * a fall: `Fallen` is the knockdown. Appended, not inserted: the wire field is
   * 6 bits (WIRE_ANIM_ENUM_BITS), so 31 costs nothing and no existing value
   * moves. The client's ANIM_CLIP table stops at 30 and `clipForAnim` returns ''
   * for anything past it, which its own contract defines as "fall back to the
   * locomotion ladder (never a T-pose)" — so this degrades safely until a
   * stagger clip is mapped there.
   */
  Stumble = 31,
  /**
   * The keeper BOWLS the ball out underarm along the deck to a near defender
   * (§4.5 "throw (short/long)"). Owner report: *"the goal keeper not only kicks
   * he can also throw the ball"*.
   *
   * A separate member from `GkThrow` (23) on purpose: GkThrow is the OVERARM
   * hurl — one arm, above the shoulder, mid-range — and a roll-out is the
   * opposite body shape, bent at the waist with the hand at grass height. The
   * sim has always had both trajectories inside `throwBall` and rendered them
   * with the SAME pose, which is exactly why "he can also throw" did not read
   * on screen. Different shape ⇒ different state ⇒ different clip.
   */
  GkRollOut = 32,
  /**
   * Law 15: a throw-in is delivered WITH BOTH HANDS, from behind and over the
   * head. Owner report: *"in throw in the ball has to be taken with hand there
   * is no aniamtion for that"*.
   *
   * This is the taker STOOD AT THE LINE with the ball raised — the part that is
   * on screen for the whole Ready window, so it is the part that makes the
   * throw-in read as a throw-in at all. The RELEASE is `Throw` (17), which has
   * existed unused since the first types.ts (constants.ts's THROW_ANIM_HOLD_S
   * says in as many words that it "drives AnimState.Throw") and is now stamped
   * by rules/fsm.ts's executeThrowIn.
   *
   * Fully DERIVED — no new PlayerArrays field. player-controller's stepAnim
   * reads it off match.phase/restartTaker/restartX/restartZ, all of which
   * already clone, hash and serialise, exactly the way goalkeeper.ts derives
   * the reaction gate rather than storing it.
   *
   * Appended, not inserted: the wire field is 6 bits (WIRE_ANIM_ENUM_BITS ⇒
   * 0..63), so 32 and 33 cost nothing and no existing value moves. Until the
   * client's ANIM_CLIP table gains rows 32/33, `clipForAnim` returns '' and its
   * own contract degrades to the locomotion ladder (never a T-pose) — safe, but
   * INVISIBLE, which is the bug being fixed. See the report's clip table.
   */
  ThrowInHold = 33,
  /**
   * Moving mostly AGAINST the facing — a defender giving ground, a player
   * repositioning while keeping his eyes on play. Selected by locomotionAnim
   * when the velocity sits more than ~120° off the body yaw (STRAFE picks up
   * 60–120°). Before this member existed the renderer had nothing to say
   * about backward travel and played the FORWARD gait cycle reversed — the
   * filmed "backward input is a rotated forward run" defect. The client maps
   * it to the authored Walk_B/Move_B/Run_B lane.
   *
   * Appended, not inserted: the wire field is 6 bits (0..63), so 34 costs
   * nothing and no existing value moves.
   */
  Backpedal = 34,
  /**
   * "THEY SLEEP ON THE BALL" — the keeper smothering a ball at ground level and
   * covering it. Owner, verbatim: *"like how real goal keeper do when they get
   * the ball they sleep on the ball"*.
   *
   * A separate member from `GkDiveLeft`/`GkDiveRight` (19/20) for the same
   * reason `GkRollOut` is separate from `GkThrow`: it is a different body
   * shape. A dive is a full-stretch lateral leap with the hands leading; a
   * smother is dropping ONTO the ball and pinning it, and rendering the two
   * with the same pose is exactly why the move did not read on screen.
   *
   * ⚠ The clip already shipped. `GkSmother` is authored, bound in
   * `clipAvailability.ts` and covered by `gkClips.test.ts` — and the client
   * listed `GK_SMOTHER` in UNREACHABLE_COMMANDS with the reason spelled out:
   * "goalkeeper.ts produces the BEHAVIOUR but tags it as a dive". This member
   * is the one missing link between an authored clip and the sim state that
   * earns it.
   *
   * Appended, not inserted: the wire field is 6 bits (0..63), so 35 costs
   * nothing and no existing value moves.
   */
  GkSmother = 35,
  /**
   * A LOW-CHARGE ground shot — the knock-ahead. Owner: "when a player is
   * surrounded by players and he cant dribble he will kick the ball front and
   * run and catch it" — that tap must not play the full wind-up strike. The
   * sim already scales the impulse by shootCharge; this publishes the same
   * split to the renderer (charge < SHOT_LIGHT_CHARGE_S), because the
   * renderer must never infer intensity from ball speed. Appended, not
   * inserted: the wire field is 6 bits and 36 fits.
   */
  ShootLight = 36,
  /**
   * BALL CONTROL BY HEIGHT (owner, 2026-08-14): "when the ball is at knee
   * height receive the ball with knee and transfer it to the feet", and "if
   * player dont click anything and just jumps when the ball is at chest
   * height let him receive the ball with chest pass it to knee then to the
   * feet". `applyFirstTouchTrap` cushioned both bands and published NO
   * animation at all, so a trap looked like the ball simply deciding to stop.
   * Two states because they are two different body shapes — the same reason
   * Throw and ThrowInHold are two states.
   */
  KneeControl = 37,
  ChestControl = 38,
  /**
   * A pass aimed to the player's RIGHT, struck with the LEFT foot (owner,
   * 2026-08-14: "if the pass is left use right foot, if pass is right use
   * left foot" - the inside-of-the-boot push across the body). Plain `Pass`
   * stays the right-footed swing. Published by the sim because the renderer
   * must never infer the side from ball velocity.
   */
  PassLeftFooted = 39,
  /**
   * THE KEEPER'S STOOP. He bends, scoops the ball off the floor and stands up
   * with it.
   *
   * Owed from the moment `resolveSave` was taught that a keeper can reach a
   * ball at his own feet (he bends for it — the claim sphere used to sit on his
   * HANDS, 1.4 m above a grounded ball, so he could never pick one up and
   * dribbled it into his own net instead). That fix had no clip behind it, so
   * he gathered a ball off the floor in the STANDING GkCatch pose. This is the
   * bend: `goalkeeper scoop.fbx`, 36 f / 1.200 s, contact at f14.
   */
  GkPickupGround = 40,
  /** X+X — bending to place the held ball on the ground and stepping off it. */
  GkPlaceBall = 41,
  /**
   * THE CARRY TURN (owner: "when player turns with the ball instead of simple
   * turn show it like a dribble turn which makes it look cool").
   *
   * A carrier changing direction hard used to keep playing the plain gait
   * while his heading swung, which reads as the model rotating rather than the
   * player turning. This is the lean and the dropped hip that make it a turn.
   */
  CarryTurn = 42,
}

// ---------------------------------------------------------------------------
// Player state — structure-of-arrays. (§15.7)
// ---------------------------------------------------------------------------

/**
 * SoA player storage. Every array is length MAX_PLAYERS and allocated once.
 * Index = PlayerId.
 *
 * Float64Array (not Float32Array) is deliberate: JS arithmetic is f64, so
 * storing f32 would round-trip through a narrowing conversion on every read
 * and write. That conversion is deterministic, but it costs precision for no
 * benefit — the wire format quantizes anyway (§2.8), and memory is not a
 * constraint at 22 entities.
 */
export interface PlayerArrays {
  /** Slot occupied at all? */
  active: Uint8Array;
  team: Uint8Array;
  /** Position (world space) */
  px: Float64Array;
  py: Float64Array;
  pz: Float64Array;
  /** Velocity */
  vx: Float64Array;
  vy: Float64Array;
  vz: Float64Array;
  /** Facing yaw, radians. Players are upright — a single angle beats a quat. */
  yaw: Float64Array;
  /** Stamina 0..100 (§3.3) */
  stamina: Float64Array;
  /** AnimState enum */
  anim: Uint8Array;
  /**
   * THE ACTION EXECUTION ID (§11). Monotonic per player, bumped every time the
   * sim ACCEPTS a new animation-visible action — a strike, a challenge, a skill,
   * a dive, a throw-in, a knockdown, or an ordinary locomotion transition.
   *
   * WHY `anim` ALONE IS NOT AN IDENTITY. `anim` is a LEVEL, not an event: the sim
   * holds `AnimState.Pass` for a whole strike window (KICK_ANIM_HOLD_S), so it is
   * seen by many render frames and many snapshots. A consumer that dedupes on the
   * VALUE cannot tell one pass from the next pass, and a consumer that dedupes on
   * the frame/tick number re-fires whenever the clock is re-read (reconciliation
   * rewinds the tick; a paused renderer repeats one). Neither is an identity.
   * This counter IS: it changes exactly once per accepted action and never twice
   * for the same one, so "one animation start, one contact event, one recovery
   * lifecycle per execution" is a pointer comparison rather than a heuristic.
   *
   * DETERMINISTIC BY CONSTRUCTION (D1/D2): a per-player integer increment off
   * existing sim state. No clock, no RNG. Cloned and hashed like every other
   * field, so a reconciliation rewind restores it exactly.
   *
   * Wrapped to `ANIM_EXEC_WIRE_BITS` on the wire (see anim-exec.ts) — consumers
   * only ever test it for INEQUALITY, never ordering.
   */
  animExecId: Int32Array;
  /**
   * The last tick this player moved above ANIM_IDLE_SPEED - the memory the
   * idle settle window (IDLE_SETTLE_S) is measured against. Initialised deep
   * in the past so a freshly created world idles immediately instead of
   * marching on the spot at kickoff.
   */
  lastMoveTick: Int32Array;
  /** Tick the current anim state was entered (stamped in stepAnim) — the
   *  memory LOCO_DWELL_S is measured against. */
  animEnterTick: Int32Array;
  /** Tick this player last entered a locomotion GAIT. The dwell is measured
   *  from here, so an action passing through cannot reset it. */
  lastGaitTick: Int32Array;
  /** Last speed band chosen (index into LOCOMOTION_BANDS). Survives strafes,
   *  touches and restart poses so the band hysteresis is not wiped by them. */
  lastBand: Int8Array;
  /** Keeper X+X: the tick of the last GkHold rising edge, and the previous
   *  tick's flag state for edge detection. Dropping the ball is irreversible
   *  under Law 12, so it takes two deliberate taps. */
  /** Last tick's MOVEMENT heading, for the carry-turn swing rate. Movement
   *  rather than facing: a carrier can look one way and travel another, and it
   *  is the change of DIRECTION that is a turn. */
  prevHeading: Float64Array;
  gkHoldTapTick: Int32Array;
  gkHoldTapPrev: Uint8Array;
  /** Normalized blend time into the current anim, 0..1 (§2.8) */
  animBlend: Float64Array;
  /** Seconds remaining of an uninterruptible action (slide recovery, skill move) */
  actionTimer: Float64Array;
  /** Active SkillMove enum */
  skillActive: Uint8Array;
  /** Seconds remaining in the current skill move */
  skillTimer: Float64Array;
  /** Yellow cards accumulated (§5.3) */
  yellows: Uint8Array;
  /** Sent off — excluded from sim but slot retained for reporting */
  sentOff: Uint8Array;
  /** True while this player has the ball inside POSSESSION_RADIUS */
  possessing: Uint8Array;
  /** Tick at which the GK first held the ball, for the 6-second rule (§5.5) */
  gkHoldStartTick: Int32Array;
  /** Charge accumulators, mirrored server-side to validate client claims */
  passCharge: Float64Array;
  shootCharge: Float64Array;
  /**
   * House rule ("funny addon", not a Law of the Game): seconds a victim stays
   * knocked to the floor after a punch/kick — planted, unsteerable, Fallen anim.
   * The victim slides to a stop from the knockback while this counts down. See
   * player-controller.ts startViolentConduct.
   */
  knockdownTimer: Float64Array;
  /**
   * Seconds a player is OFF BALANCE after losing a shoulder barge (§3.3, §4.4).
   *
   * Deliberately NOT knockdownTimer: a knockdown plants the player and removes
   * steering entirely, and a fair charge does not put anyone on the floor. This
   * is the softer reaction — full steering, a scrubbed speed cap
   * (STUMBLE_SPEED_MULTIPLIER) and no new action may be STARTED while it runs.
   * Keeping steering is not a detail: two overlapping capsules that both stop
   * steering is exactly how a match deadlocks.
   *
   * Cloned and hashed (world.ts) like every other sim field. NOT on the wire, by
   * the same rule as knockdownTimer/actionTimer (net/snapshot.ts's header: the
   * snapshot is a render+predict view, not a WorldState mirror) — the client
   * derives its own copy from the serialised positions/velocities it re-steps,
   * and the VISUAL reaches it through `anim` = AnimState.Stumble, which IS
   * serialised.
   */
  stumbleTimer: Float64Array;
  /**
   * THE CARRY-RECOVERY LATCH — 1 while this player has run off his own ball and
   * has not yet got back on it. (§3.3 / §3.4)
   *
   * ⚠ WITHOUT A LATCH THE GOVERNOR CHATTERS, AND THE CHATTER IS THE DEFECT.
   * `carryHasBrokenDown` is a geometric test — is the ball out of foot reach AND
   * no longer ahead along my running line — and a carrier drifting beside his
   * ball crosses that line repeatedly. Each crossing swings the speed cap
   * between full pace and CARRY_RECOVER_MIN_SPEED, the gait ladder follows the
   * cap, and the body flips clip several times a second. Filmed by the owner as
   * "randomly jumping when he has the ball" and "slowing down idk why" — one
   * cause, read as two defects.
   *
   * So the test ENGAGES the latch and only recovering the ball CLEARS it: the
   * cap stays governed continuously until the ball is genuinely back at his
   * boot, which is also the only reading of "he has to check his run and get
   * back on it" that a player can act on.
   *
   * Serialised like every other player field, so prediction, reconciliation and
   * the hash all carry it (D1).
   */
  carryRecovering: Uint8Array;
  /**
   * 1 when the character controller reports the capsule STANDING ON something,
   * 0 when it is genuinely off the ground. Rapier's `computedGrounded()`, kept
   * instead of discarded.
   *
   * ⚠ WHY THIS EXISTS RATHER THAN A HEIGHT TEST. Airborne used to mean
   * `py > JUMP_GROUND_EPSILON`, and the owner filmed the consequence as *"he
   * randomly jumps"*. The pitch is not flat — PITCH_UNDULATION_M is 0.03 m —
   * so a player CRESTING A BUMP at a run is lifted to a measured 0.053 m and
   * the anim selector called it a jump. Measured over 32 880 frames of driven
   * play in which Space is never pressed: 37 frames of AnimState.Jump.
   *
   * No threshold can fix that, which is the whole point. A real jump's FIRST
   * airborne tick only clears 0.048 m — BELOW the 0.053 m crest — so the two
   * populations overlap in height and are separable only by whether the
   * capsule is still resting on the pitch. The physics engine already answers
   * exactly that question every tick.
   *
   * Written by `moveViaPhysics` for every player it moves; a player it skips
   * keeps his last value, which is why this initialises to 1 (grounded) rather
   * than 0 — the wrong default here is a phantom jump, the same defect.
   */
  grounded: Uint8Array;
  /**
   * 1 from the tick a player LAUNCHES a jump until the tick he lands. The only
   * thing that sets it is `tryJump`; the only thing that clears it is touching
   * down.
   *
   * ⚠ THIS EXISTS BECAUSE "AIRBORNE" AND "JUMPING" ARE NOT THE SAME THING, and
   * treating them as one is the whole of the owner's "he randomly jumps".
   * Gating the Jump anim on `grounded === 0 && py > JUMP_GROUND_EPSILON` still
   * left 21 phantom frames in a driven capture, and the trace says exactly what
   * they are — a player CRESTING a bump and falling off the far side:
   *
   *     py=0.0094 gnd=1 vy=0        ← flat grass
   *     py=0.0388 gnd=1 vy=0        ← up onto the crest, still in contact
   *     py=0.0360 gnd=0 vy=-0.164   ← off the far side. AIRBORNE, and falling.
   *     py=0.0306 gnd=0 vy=-0.327   ← one more tick of gravity
   *     py=0.0224 gnd=0 vy=-0.491   ← back down
   *
   * Every one of those is genuinely not-grounded and genuinely above the
   * epsilon. It is a 3 cm fall lasting 33 ms, and no threshold on height or
   * contact can tell it from a jump — because it IS a small fall. What
   * separates them is that a jump was LAUNCHED: `tryJump` seeds vy upward. So
   * the animation asks that question directly instead of inferring it, and the
   * Jump anim now needs no height test at all.
   */
  jumpAirborne: Uint8Array;
  /**
   * House rule: per-player punch+kick offence count. Every punch/kick is an
   * unconditional foul; the 4th (count EXCEEDS PUNCH_KICK_OFFENSE_LIMIT) awards a
   * penalty to the victim's team regardless of location, then resets to 0. This
   * is §5.3's "track per-player foul count; escalate", pushed to a house extreme.
   */
  punchKickOffenses: Uint8Array;
  // ── §2 contact-timed action state (FootballActionController). Only live when
  // WorldState.contactTiming is on; otherwise every field stays at its idle
  // default, so competitive play is byte-identical. Cloned + hashed like all
  // sim state. ──
  /** Active ActionKind (0 = None). */
  actKind: Uint8Array;
  /** Tick the action started (-1 = idle). Phase is derived from this + the def. */
  actStartTick: Int32Array;
  /** Monotonic per-player execution id — bumped on each new action, so a client
   *  can reject stale/duplicate contact events across pause/frame boundaries. */
  actExecId: Int32Array;
  /** 1 once the ball impulse has fired for this execution (fire-once guard). */
  actContacted: Uint8Array;
  /** Release snapshot applied AT contact: aim yaw, power (charge s), curl. */
  actAimYaw: Float64Array;
  actPower: Float64Array;
  actCurl: Float64Array;
  /**
   * Was this committed strike CHIPPED (the loft modifier held at release)?
   * Contact timing defers the impulse and rebuilds the input from these
   * stored fields; flags are reconstructed from the ActionKind, and no kind
   * distinguishes a chip from a flat shot - so without this the loft was
   * silently dropped on every deferred SHOT (owner, 2026-08-15). 1 = chipped.
   */
  actChip: Uint8Array;
  /** Selected contact socket for the action (a ContactSocket value). */
  actFoot: Uint8Array;
  /** Selected pass target PlayerId at release (-1 = none / shot). */
  actTarget: Int32Array;
}

export function createPlayerArrays(): PlayerArrays {
  return {
    active: new Uint8Array(MAX_PLAYERS),
    team: new Uint8Array(MAX_PLAYERS),
    px: new Float64Array(MAX_PLAYERS),
    py: new Float64Array(MAX_PLAYERS),
    pz: new Float64Array(MAX_PLAYERS),
    vx: new Float64Array(MAX_PLAYERS),
    vy: new Float64Array(MAX_PLAYERS),
    vz: new Float64Array(MAX_PLAYERS),
    yaw: new Float64Array(MAX_PLAYERS),
    stamina: new Float64Array(MAX_PLAYERS),
    anim: new Uint8Array(MAX_PLAYERS),
    animExecId: new Int32Array(MAX_PLAYERS),
    lastMoveTick: new Int32Array(MAX_PLAYERS).fill(-600),
    animEnterTick: new Int32Array(MAX_PLAYERS).fill(-600),
    lastGaitTick: new Int32Array(MAX_PLAYERS).fill(-600),
    lastBand: new Int8Array(MAX_PLAYERS),
    prevHeading: new Float64Array(MAX_PLAYERS),
    gkHoldTapTick: new Int32Array(MAX_PLAYERS).fill(-600),
    gkHoldTapPrev: new Uint8Array(MAX_PLAYERS),
    animBlend: new Float64Array(MAX_PLAYERS),
    actionTimer: new Float64Array(MAX_PLAYERS),
    skillActive: new Uint8Array(MAX_PLAYERS),
    skillTimer: new Float64Array(MAX_PLAYERS),
    yellows: new Uint8Array(MAX_PLAYERS),
    sentOff: new Uint8Array(MAX_PLAYERS),
    possessing: new Uint8Array(MAX_PLAYERS),
    gkHoldStartTick: new Int32Array(MAX_PLAYERS),
    passCharge: new Float64Array(MAX_PLAYERS),
    shootCharge: new Float64Array(MAX_PLAYERS),
    knockdownTimer: new Float64Array(MAX_PLAYERS),
    stumbleTimer: new Float64Array(MAX_PLAYERS),
    carryRecovering: new Uint8Array(MAX_PLAYERS),
    // Starts GROUNDED: every spawn places a capsule on the pitch, and a 0 here
    // would read as one tick of freefall on the first frame of every match.
    grounded: new Uint8Array(MAX_PLAYERS).fill(1),
    jumpAirborne: new Uint8Array(MAX_PLAYERS),
    punchKickOffenses: new Uint8Array(MAX_PLAYERS),
    actKind: new Uint8Array(MAX_PLAYERS),
    actStartTick: new Int32Array(MAX_PLAYERS).fill(-1),
    actExecId: new Int32Array(MAX_PLAYERS),
    actContacted: new Uint8Array(MAX_PLAYERS),
    actAimYaw: new Float64Array(MAX_PLAYERS),
    actPower: new Float64Array(MAX_PLAYERS),
    actCurl: new Float64Array(MAX_PLAYERS),
    actChip: new Uint8Array(MAX_PLAYERS),
    actFoot: new Uint8Array(MAX_PLAYERS),
    actTarget: new Int32Array(MAX_PLAYERS).fill(-1),
  };
}

// ---------------------------------------------------------------------------
// Ball state (§3.2)
// ---------------------------------------------------------------------------

export interface BallState {
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  /** Angular velocity (rad/s). Drives the Magnus force — the soul of the game. */
  wx: number; wy: number; wz: number;
  /** Orientation, for rendering the ball's spin. */
  qx: number; qy: number; qz: number; qw: number;
  /** PlayerId of the last player to touch, or -1. Decides throw-in/corner. (§5.2) */
  lastTouchedBy: number;
  /** Team of the last toucher, or -1. */
  lastTouchedTeam: number;
  /** Tick of the last touch. */
  lastTouchTick: number;
  /** PlayerId currently in possession, or -1. */
  possessedBy: number;
  /**
   * §1 controlled-dribble GATHER owner: the player whose knock this loose ball
   * still belongs to, or -1. Unlike `possessedBy` it SURVIVES the brief blip
   * where a knock-and-run touch runs the ball just past the tight capture gate —
   * so the gather touch can pull it back instead of dying the instant it escapes.
   * A KICK (applyStrike) clears it to -1, which is what makes a pass/shot fly
   * free; a rival winning possession reassigns it. Fully sim-derived (identical
   * on client and server), so it is safe to hash.
   */
  gatherOwner: number;

  // ── CONTROL STATE (Phase-1 Commit 2) ──────────────────────────────────
  //
  // The audit found that two players can sit inside POSSESSION_RADIUS at the
  // same time — 14 ticks of it on the probe's 50/50 — and `bestDist` resolved
  // it instantly to whoever's centre was marginally nearer. There was no way
  // to say "this ball is being fought over", so nothing downstream could
  // reason about a contest.
  //
  // DERIVED, NOT REPLICATED. Both fields are recomputed from scratch every
  // tick by `updatePossession` out of state that is already hashed and already
  // on the wire, so the snapshot is untouched — the sim is deterministic and
  // derived state is preferable to another protocol migration (the boost bits
  // were a fresh reminder of what those cost). They are hashed anyway: a peer
  // that derives a different contest from identical inputs has a desync, and
  // this is where it surfaces rather than three ticks later.

  /** `BallControl` — Free / Contested / Controlled. */
  controlState: number;
  /**
   * Ticks the current contest has run. A contest cannot last forever or the
   * ball never gets played, so it is a countdown to a decision, not a
   * stalemate: past BALL_CONTEST_MAX_TICKS the better control score takes it.
   */
  contestTicks: number;
  /** True while resting on the ground (affects spin decay + rolling resistance). */
  grounded: number;
  /**
   * True from the instant a shot/pass launched EITHER while its striker had
   * boost tier 2 active (gated the same "stamina must be above 0" rule the
   * aura/lightning VFX use — see soloPractice.ts/practice.ts's `setBoostAura`
   * gating) OR at full charge (the charge fraction hit 1) — owner, 2026-08-15,
   * on being asked whether the wave should also show on an ordinary full-power
   * kick: *"also on full power"*. NOT the same condition that grants
   * BOOST2_KICK_POWER_MULTIPLIER — that stays strictly boost-tier-2-only, so a
   * full-power non-boosted kick shows the wave without a stealth speed buff it
   * was never asked to get. Presentation-only: it exists so the render layer
   * (a red trail "wave", owner 2026-08-15) can tell a power strike from an
   * ordinary one without re-deriving it from input flags the renderer never
   * sees. `applyStrike` overwrites it on EVERY kick, power or not, so it
   * always reflects the CURRENT flight and clears itself the moment anyone
   * next touches the ball — no separate timer. On the wire (net/snapshot.ts's
   * `BF_POWERKICK`) for remote players' balls in online matches.
   */
  powerKick: boolean;
}

export function createBallState(): BallState {
  return {
    px: 0, py: 0, pz: 0,
    vx: 0, vy: 0, vz: 0,
    wx: 0, wy: 0, wz: 0,
    qx: 0, qy: 0, qz: 0, qw: 1,
    lastTouchedBy: -1,
    lastTouchedTeam: -1,
    lastTouchTick: -1,
    possessedBy: -1,
    gatherOwner: -1,
    controlState: BallControl.Free,
    contestTicks: 0,
    grounded: 1,
    powerKick: false,
  };
}

// ---------------------------------------------------------------------------
// Match state machine (§5.1)
// ---------------------------------------------------------------------------

/**
 * What is happening to the ball, as opposed to who owns it. (Phase-1 Commit 2)
 *
 * Deliberately THREE states and not the full FREE → CONTROL_ATTEMPT →
 * CONTROLLED chain: the owner's instruction was to start with candidacy rather
 * than a large state machine, and a CONTROL_ATTEMPT that nothing yet consumes
 * would be a state that exists to look complete. It is added when a system
 * needs it.
 */
export const enum BallControl {
  /** Nobody owns it and nobody is close enough to be fighting for it. */
  Free = 0,
  /** Two or more players have a real claim and none of them clearly wins. */
  Contested = 1,
  /** One player has it. */
  Controlled = 2,
}

export const enum MatchPhase {
  Warmup = 0,
  Kickoff = 1,
  InPlay = 2,
  BallOut = 3,
  ThrowIn = 4,
  Corner = 5,
  GoalKick = 6,
  FreeKick = 7,
  Penalty = 8,
  DropBall = 9,
  Goal = 10,
  Offside = 11,
  HalfTime = 12,
  FullTime = 13,
  ExtraTime = 14,
  PenaltyShootout = 15,
}

/** Every restart has: setup (positioning) → ready → live. (§5.1) */
export const enum RestartStage {
  Setup = 0,
  Ready = 1,
  Live = 2,
}

export interface MatchState {
  phase: MatchPhase;
  restartStage: RestartStage;
  /** Ticks remaining in the current setup/ready stage. */
  stageTimer: number;
  /** 1 or 2. */
  half: number;
  /** Elapsed match ticks in the current half. */
  clockTicks: number;
  /** Accumulated stoppage from injuries/subs/goals/VAR/cards. (§5.5) */
  addedTimeTicks: number;
  scoreA: number;
  scoreB: number;
  /** Team awarded the current restart, or -1. */
  restartTeam: number;
  /** Where the restart is taken from. */
  restartX: number;
  restartZ: number;
  /** PlayerId designated to take the restart, or -1. */
  restartTaker: number;
  /** Team in possession, or -1. */
  possessionTeam: number;
  /**
   * The restart phase the next open-play kick belongs to, or InPlay when none is
   * pending. Set when a restart goes Live and consumed by the first strike, so
   * resolveKicks can tag that kick's offside snapshot as taken FROM a throw-in /
   * corner / goal-kick — the Law-11 "no offside directly from a restart"
   * exemption (§5.4). InPlay ⇒ ordinary open play. (Persisted: a taker may walk
   * to the ball across several ticks before striking.)
   */
  restartKickPhase: MatchPhase;
  /** Ticks remaining on an advantage window; 0 = none. (§5.3) */
  advantageTicks: number;
  /** The foul held pending an advantage outcome. */
  pendingFoulPlayer: number;
  pendingFoulSeverity: number;
  /**
   * REFEREE REACTION (§5.6): ticks left before the whistle for a foul the
   * referee has already DECIDED to give. 0 = nothing pending.
   *
   * A real referee sees the offence, then blows — `evaluateFoulsAndCards` used
   * to detect, card and restart in one tick, so play stopped on the same frame
   * the tackle landed. The delay is drawn from the sim RNG in
   * `rules/fouls.ts armWhistle` (REFEREE_REACTION_MIN_MS..MAX_MS converted to
   * whole ticks) and counted down there; when it hits 0 the free kick / penalty
   * is staged exactly as before.
   *
   * It is NOT a second advantage window: `handleFoul` decides advantage FIRST
   * and only the no-advantage path arms this, so the two never stack.
   *
   * Ticks, not seconds, and hashed — the countdown must be bit-identical on
   * every peer. The card is still applied at the moment of the offence, so the
   * FOUL event (and its banner) is unaffected; only the restart waits.
   */
  pendingWhistleTicks: number;
  /** Offender held by the pending whistle, or -1. */
  pendingWhistleFouler: number;
  /** Team the pending restart favours (the FOULED side), or -1. */
  pendingWhistleTeam: number;
  /** Spot the pending restart will be taken from. */
  pendingWhistleX: number;
  pendingWhistleZ: number;
  /** 0..1, drives crowd audio + camera shake. (§11) */
  excitement: number;

  // ── PENALTY SHOOTOUT (§5.1) — "penality if draw" ─────────────────────────
  //
  // `MatchPhase.PenaltyShootout` was in the enum from the start with nothing
  // behind it; these are its state. All integers, all hashed: a shootout has
  // to resolve identically on every peer or two clients disagree about who won
  // the match.
  //
  // The match SCORE is never touched by a shootout — a 2-2 that goes to
  // penalties is recorded as 2-2, with the shootout as its own tally, exactly
  // as a real result reads.

  /** 1 while a shootout is running. Read by ball-out.ts, which treats a ball
   *  crossing a line completely differently inside one. */
  shootoutActive: number;
  /** Kicks taken, per team. Sudden death continues past the regulation five. */
  shootoutKicksA: number;
  shootoutKicksB: number;
  /** Shootout goals, per team — NOT scoreA/scoreB. */
  shootoutGoalsA: number;
  shootoutGoalsB: number;
  /** Coin toss: which team kicks first, and therefore who kicks on a tie in
   *  kicks taken. */
  shootoutFirstTeam: number;
  /** Ticks the current attempt has been live, against the backstop that stops
   *  a wedged ball or an absent taker holding the match open forever. */
  shootoutAttemptTicks: number;
}

export function createMatchState(): MatchState {
  return {
    phase: MatchPhase.Warmup,
    restartStage: RestartStage.Setup,
    stageTimer: 0,
    half: 1,
    clockTicks: 0,
    addedTimeTicks: 0,
    scoreA: 0,
    scoreB: 0,
    restartTeam: -1,
    restartX: 0,
    restartZ: 0,
    restartTaker: -1,
    possessionTeam: -1,
    restartKickPhase: MatchPhase.InPlay,
    advantageTicks: 0,
    pendingFoulPlayer: -1,
    pendingFoulSeverity: 0,
    pendingWhistleTicks: 0,
    pendingWhistleFouler: -1,
    pendingWhistleTeam: -1,
    pendingWhistleX: 0,
    pendingWhistleZ: 0,
    excitement: 0,
    shootoutActive: 0,
    shootoutKicksA: 0,
    shootoutKicksB: 0,
    shootoutGoalsA: 0,
    shootoutGoalsB: 0,
    shootoutFirstTeam: 0,
    shootoutAttemptTicks: 0,
  };
}

// ---------------------------------------------------------------------------
// Offside snapshot (§5.4)
// ---------------------------------------------------------------------------

/**
 * Captured at the exact tick a teammate plays the ball. The offside decision is
 * made against THIS, not against live positions — that is the whole law.
 * Flagged receivers are not whistled until they interfere. (§5.4)
 */
export interface OffsideSnapshot {
  /** Tick the ball was played. -1 = no live snapshot. */
  tick: number;
  /** Team that played the ball. */
  team: number;
  /** Player who played it. */
  passer: number;
  /** Per-player: was this player in an offside POSITION at that tick? */
  offsidePosition: Uint8Array;
  /** The offside line (world Z or X depending on attack direction), for the VAR overlay. */
  lineCoord: number;
  /** Restart type the ball came from — throw-in/goal-kick/corner exempt. (§5.4) */
  fromRestart: MatchPhase;
}

export function createOffsideSnapshot(): OffsideSnapshot {
  return {
    tick: -1,
    team: -1,
    passer: -1,
    offsidePosition: new Uint8Array(MAX_PLAYERS),
    lineCoord: 0,
    fromRestart: MatchPhase.InPlay,
  };
}

// ---------------------------------------------------------------------------
// Match configuration — pitch size, rules and formations are DATA. (§9.2)
// One codebase, all modes.
// ---------------------------------------------------------------------------

export type SquadSize = 1 | 2 | 3 | 4 | 5 | 7 | 11;

/**
 * ONE TEAM'S KICKOFF LINEUP. (§8.2, §8.3)
 *
 * `slots[i]` is the §8.2 position code the player in team slot `i` takes the
 * field in, and team slot `i` IS `PlayerId = team * squadSize + i` (sim/roster.ts
 * `spawnSquads`). That identity is the whole wiring: a front end that stores its
 * XI as "slot → my player id" (ui/tournament-store.ts) and hands the matching
 * "slot → Position" here has, by construction, put its chosen player on the
 * chosen position — no name ever has to enter the sim.
 *
 * The vocabulary is the sim's own `Position`, never a parallel one. There is no
 * second formation model here either: "the roster IS the formation"
 * (sim/formation-anchors.ts header), so a list of eleven position codes is a
 * complete statement of shape and the AI's Layer 3 already understands it.
 *
 * `formationId` is PROVENANCE ONLY — the front-end key ('442', '4231') that
 * produced `slots`. Nothing in the sim reads it; it exists so a log line, a HUD
 * label or a bug report can say which shape was picked without reverse-
 * engineering it from eleven codes. Never branch on it in /sim.
 */
export interface TeamRoster {
  /** Front-end formation key. Provenance only — the sim reads `slots`. */
  formationId: string;
  /** Position per team slot. Length SHOULD equal `MatchConfig.squadSize`. */
  slots: readonly Position[];
}

/**
 * Both lineups for one match. Optional on `MatchConfig`: absent means "use the
 * built-in per-squad-size default", which is exactly what every mode did before
 * this existed, so every existing config, test and mode is unaffected.
 * (sim/roster.test.ts pins that compatibility explicitly.)
 */
export interface MatchRoster {
  /** Index = `Team`: 0 = team A, 1 = team B. */
  teams: readonly [TeamRoster, TeamRoster];
}

export interface MatchConfig {
  squadSize: SquadSize;
  /** Pitch dimensions in metres. (§9.2) */
  pitchLength: number;
  pitchWidth: number;
  hasGoalkeepers: boolean;
  offsideEnabled: boolean;
  foulsEnabled: boolean;
  /** Seconds per half. (§5.5) */
  halfLengthS: number;
  /** Air density — altitude is a match setting. (§3.2) */
  airDensity: number;
  /** Wet pitch modifies friction/restitution. (§3.2) */
  wetPitch: boolean;
  /** Deterministic seed for the whole match. (§15.3) */
  seed: number;
  varEnabled: boolean;
  substitutionsPerTeam: number;
  /**
   * A level match goes to a penalty shootout instead of stopping at FullTime
   * (owner, 2026-08-16: "penality if draw"). The score itself is untouched —
   * the shootout carries its own tally and decides the winner. (§5.1)
   */
  shootoutOnDraw: boolean;
  /**
   * §4 KICK DIRECTIVE gates (owner, 2026-07-25) — see the KICK DIRECTIVE block at
   * the end of constants.ts for the full reasoning. Both DEFAULT to the shipped
   * values `CURVE_ENABLED` / `STRIKE_AIM_ERROR_ENABLED` (both false) in
   * createMatchConfig, so production behaviour is decided in ONE place and a
   * caller never has to know these exist.
   *
   * They live here, on config, purely so the gated mechanics stay TESTABLE: the
   * curve and strike-error suites build a world with the gate ON and keep proving
   * the mechanic works, instead of asserting the dead path and rotting. Rules
   * toggles already live on config (offsideEnabled, foulsEnabled, varEnabled), so
   * this is the existing pattern, not a new one.
   *
   * NOT ON THE WIRE, deliberately: the Welcome frame (net/welcome.ts) is
   * bit-packed and adding a field is a PROTOCOL BREAK. It does not need to carry
   * these because both sides derive them from the same module constants in the
   * same build — and the Welcome's build fingerprint already rejects a client
   * whose bundle differs from the server's. If you ever set either flag from
   * anything other than the constant (a lobby option, a per-room setting), it MUST
   * go on the wire and PROTOCOL_VERSION must be bumped, or the client will predict
   * a curve the server never applied.
   */
  curveEnabled: boolean;
  strikeAimErrorEnabled: boolean;
  /**
   * THE CHOSEN XI (§8.3). Absent ⇒ the built-in default layout for `squadSize`
   * (sim/roster.ts DEFAULT_ROSTERS) — i.e. byte-identical to the behaviour that
   * shipped before this field existed. That default is the compatibility
   * guarantee and it is asserted, not assumed: sim/roster.test.ts.
   *
   * It lives on CONFIG, not on WorldState, and that is the load-bearing choice.
   * A roster is a match SETTING, decided once before kickoff and never touched
   * again by a tick; putting it in WorldState would mean adding it to
   * `cloneWorldInto` AND `hashWorld` for state that can never change, and would
   * make every rewind copy eleven strings twice. On config it is copied by
   * `copyMatchConfig` (world.ts) — which it MUST be, or a reconciliation rewind
   * would rebuild a different eleven than prediction did — and excluded from the
   * hash for the same reason every other config field is (world.ts hashWorld:
   * "two sims with different configs are different sims, not a desync").
   *
   * PURELY STRUCTURAL: it changes WHERE `spawnSquads` puts players at tick 0 and
   * what `world.positions[]` says. It draws no RNG and adds no per-tick branch,
   * so `sim/determinism.test.ts` is untouched by construction.
   *
   * NOT ON THE WIRE, and it does not need to be. The Welcome frame already
   * carries a per-player `position` for every active slot (net/welcome.ts
   * `rosterFromWorld` / `applyRosterToWorld`) and the first full snapshot carries
   * the spawn kinematics — so a roster-built server world reaches the client
   * through channels that already exist. `PROTOCOL_VERSION` therefore does NOT
   * move for this field. See net/welcome.ts's header before changing that.
   */
  roster?: MatchRoster;
}


/** Per §9.2. Pitch size, rules and formations all flow from this table. */
export const SQUAD_PRESETS: Record<SquadSize, Pick<MatchConfig,
  'pitchLength' | 'pitchWidth' | 'hasGoalkeepers' | 'offsideEnabled'>> = {
  // ⚠ ONE PITCH, EVERY SQUAD SIZE (owner, 2026-08-16: "no matter the number of
  // players pitch size should be same"). These used to shrink with the squad —
  // 5v5 played on 75 x 50 — while the STADIUM always paints 105 x 68. The goals
  // are drawn at the SIM's goal line, so on a small-sided match they stood
  // fifteen metres inside the painted grass: "no net is in middle of the pitch",
  // "goal post should be at the end of the pitch". There is one bowl and one set
  // of markings, so there is one pitch. Fewer players now means more space, not
  // a different ground.
  1: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: false, offsideEnabled: false },
  2: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: false },
  3: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: false },
  4: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: false },
  5: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: false },
  7: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: true },
  11: { pitchLength: 105, pitchWidth: 68, hasGoalkeepers: true, offsideEnabled: true },
};

// ---------------------------------------------------------------------------
// Rule events — each rule is a pure function (worldState) => RuleEvent | null (§5)
// ---------------------------------------------------------------------------

export const enum FoulSeverity {
  Clean = 0,
  Careless = 1, // → free kick
  Reckless = 2, // → yellow
  Excessive = 3, // → red
  DOGSO = 4, // → red
}

/**
 * The card the referee ACTUALLY SHOWED for an offence — which is not always the
 * card the severity alone implies. (§5.3)
 *
 * The whole reason this exists: a SECOND Reckless foul is a sending-off
 * (`applyCard` sets `sentOff` once `yellows >= YELLOWS_FOR_RED`) while the
 * event's severity stays `Reckless`, so every consumer that derived the card
 * from severity showed a YELLOW for a red-card moment. `rules/fouls.ts` now
 * stamps the issued card on the event, so the event tells the truth at the
 * point a consumer reads it.
 *
 * Ordered by weight, so `card >= RefereeCard.Yellow` and `>= Red` are both
 * meaningful tests. A second yellow is reported as `Red`, which is the card that
 * was shown. If a client ever wants the yellow-then-red presentation it needs a
 * separate signal: the snapshot carries the offender's `sentOff` flag but NOT
 * his yellow count, so "was this his second booking" is not currently derivable
 * on the wire.
 */
export const enum RefereeCard {
  None = 0,
  Yellow = 1,
  Red = 2,
}

export type RuleEventType =
  | 'GOAL'
  | 'BALL_OUT_SIDE'
  | 'BALL_OUT_GOAL_LINE'
  | 'FOUL'
  | 'OFFSIDE'
  | 'HANDBALL'
  | 'BACK_PASS'
  | 'SIX_SECOND'
  | 'ADVANTAGE'
  | 'HALF_TIME'
  | 'FULL_TIME'
  /**
   * PENALTY SHOOTOUT (§5.1). `SHOOTOUT_START.team` is the side that kicks
   * first (the coin toss); SCORED/MISSED carry the kicking team and the taker;
   * `SHOOTOUT_END.team` is the winner. The banner and the tally on the HUD
   * ride these — the match score itself never moves during a shootout.
   */
  | 'SHOOTOUT_START'
  | 'SHOOTOUT_SCORED'
  | 'SHOOTOUT_MISSED'
  | 'SHOOTOUT_END'
  /**
   * House rule ("funny addon", not a Law of the Game): the attacker struck an
   * opponent directly (P / K). Distinct from FOUL so the client can fire its own
   * VFX and rules/fouls.ts can run the always-foul + offence-counter escalation.
   * `player` = offender, `victim` = struck opponent, `team` = victim's team
   * (the side the resulting free kick / penalty favours). See DECISIONS.md D3.
   */
  | 'PUNCH'
  | 'KICK'
  /**
   * THE REFEREE'S WHISTLE to restart play (Law 5; §5.1, §5.6). Owner report:
   * *"the ball is in game only after refree whisely for start off , throwin ,
   * foul , corner / players has to wait for the whistle to start the game"*.
   *
   * Raised by rules/fsm.ts on the RestartStage.Setup→Ready flip — the exact tick
   * the ceremony ends and the designated taker first becomes allowed to play the
   * ball, which is one tick BEFORE the earliest possible strike. That ordering is
   * the point: a real referee whistles before the kick, not on it.
   *
   * Every staged restart raises it: Kickoff (both halves), ThrowIn, Corner,
   * GoalKick, FreeKick, Penalty, DropBall. Fields: `phase` = which restart is
   * being signalled (so a client can pick a whistle pattern), `team` = the side
   * taking it, `player` = the designated taker (-1 if none), `x`/`z` = the spot.
   *
   * Nothing in the SIM consumes it — it is an announcement of a transition the
   * FSM makes either way, so it cannot change the match and cannot desync.
   */
  | 'WHISTLE';

export interface RuleEvent {
  type: RuleEventType;
  tick: number;
  /** Team the decision favours, or -1. */
  team: number;
  /** Primary player involved (scorer, fouler, offside receiver), or -1. */
  player: number;
  /** Secondary player (fouled), or -1. */
  victim?: number;
  severity?: FoulSeverity;
  /**
   * The card actually issued for this offence, stamped by `rules/fouls.ts` on
   * FOUL / PUNCH / KICK / HANDBALL. Absent (undefined) on every other event type
   * and on an event no rule has consumed yet.
   *
   * PREFER THIS over deriving a card from `severity`: severity says how bad the
   * challenge was, `card` says what the referee produced, and for a second
   * bookable offence those disagree. For the same reason `severity` on an event
   * that earned a red is reported as at least `Excessive` — so a consumer that
   * has not migrated to this field still shows a red rather than a yellow.
   */
  card?: RefereeCard;
  /** Restart location. */
  x?: number;
  z?: number;
  /** For the VAR-style offside overlay. (§5.4) */
  offsideLine?: number;
  /**
   * For 'WHISTLE': the restart phase the referee has just signalled, so a client
   * can pick the right whistle pattern and HUD cue without having to diff
   * `match.phase` itself. Unused by every other event type.
   */
  phase?: MatchPhase;
}

// ---------------------------------------------------------------------------
// The world (§1.4)
// ---------------------------------------------------------------------------

/**
 * The complete authoritative state of a match at one tick.
 *
 * INVARIANT: cloneable by buffer copy, hashable by byte walk, and fully
 * determines the next tick given inputs. If you add a field, add it to
 * cloneWorld() and hashWorld() in world.ts, or the determinism test (§13.1)
 * will pass while reconciliation silently rots. That is the exact failure mode
 * §15.3 warns about.
 */
export interface WorldState {
  tick: number;
  config: MatchConfig;
  players: PlayerArrays;
  ball: BallState;
  match: MatchState;
  offside: OffsideSnapshot;
  /** PCG32 state. NEVER Math.random() in the sim. (§15.3) */
  rng: Pcg32;
  /** Attributes are static per match — not part of the hashed dynamic state. */
  attributes: Attributes[];
  /** Static per match. */
  positions: Position[];
  roles: Role[];
  /** Which team attacks +X. Flips at half time. */
  teamAAttacksPositiveX: boolean;
  /**
   * §2 contact-timed actions: when true, kicks/tackles resolve their ball impulse
   * at the animation CONTACT frame (FootballActionController) instead of on the
   * input tick. Default false — competitive play is byte-identical until this is
   * validated in Training and migrated. Set at world build (Solo Practice sets it),
   * never mid-match, so client and server always agree.
   */
  contactTiming: boolean;
}

/** Events produced by a tick. Drained by the caller; never accumulates. */
export interface StepResult {
  events: RuleEvent[];
}

/**
 * The single tick function. (§1.4)
 * MUST be pure with respect to `inputs` and MUST NOT read wall-clock time,
 * Math.random, or any ambient state. Mutates `state` in place for zero
 * allocation (§15.7) and returns the events raised this tick.
 */
export type StepFn = (
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
) => void;
